import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { X, Clock, AlertCircle } from 'lucide-react'
import { get } from '@/lib/api'
import { cn } from '@/lib/utils'

interface TrialStatus {
  plan: string
  trialEndsAt: string | null
  daysRemaining: number | null
  isExpired: boolean
  isTrial: boolean
}

const DISMISS_KEY = 'vela.trial-banner.dismissed'

export function TrialBanner() {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(() =>
    typeof sessionStorage === 'undefined' ? false : sessionStorage.getItem(DISMISS_KEY) === '1',
  )
  const { data } = useQuery({
    queryKey: ['billing', 'trial'],
    queryFn: () => get<TrialStatus>('/api/v1/billing/trial', { background: true }).catch(() => null),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  })

  useEffect(() => {
    if (dismissed) sessionStorage.setItem(DISMISS_KEY, '1')
  }, [dismissed])

  if (dismissed || !data || !data.isTrial) return null

  const expired = data.isExpired

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-b px-4 py-2 md:px-8',
        expired
          ? 'border-neg/15 bg-neg-soft'
          : 'border-[rgba(79,140,255,0.15)] bg-[linear-gradient(90deg,rgba(234,243,255,0.95),rgba(255,255,255,0.92))]',
      )}
    >
      <p
        className={cn(
          'flex items-center gap-2 text-[13px] font-medium',
          expired ? 'text-neg' : 'text-heading',
        )}
      >
        {expired ? (
          <AlertCircle className="h-4 w-4 shrink-0" />
        ) : (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--vela-ice)] text-[var(--vela-primary)] ring-1 ring-[rgba(79,140,255,0.15)]">
            <Clock className="h-3.5 w-3.5" />
          </span>
        )}
        {expired
          ? 'Your trial has ended — upgrade to keep using Vela.'
          : `${data.daysRemaining} day${data.daysRemaining === 1 ? '' : 's'} left in your free trial`}
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          onClick={() => navigate('/settings')}
          className={cn(
            'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
            expired
              ? 'bg-neg text-white hover:opacity-90'
              : 'border border-[rgba(79,140,255,0.20)] bg-card text-[var(--vela-primary-hover)] shadow-[0_1px_2px_rgba(7,24,46,0.04)] hover:border-[rgba(79,140,255,0.40)] hover:bg-[var(--vela-ice)]',
          )}
        >
          {expired ? 'Upgrade now' : 'Upgrade plan'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className={cn(
            'rounded-md p-1 transition-colors',
            expired ? 'text-neg hover:bg-neg/10' : 'text-muted hover:bg-[var(--vela-ice)] hover:text-heading',
          )}
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  )
}
